import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../components/AuthContext";
import { CATEGORY_LABELS, formatDate } from "../lib/categories";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sparkles, Plus, Pencil, Trash2, LogOut, Upload, ArrowLeft, Loader2, Copy, Waves, User, Star, ChevronLeft, ChevronRight, Crop, X } from "lucide-react";
import Cropper from "react-easy-crop";
import { toast } from "sonner";

const EMPTY_FORM = { title: "", category: "fiske", excerpt: "", body: "", images: [], published: false, featured: false, caption: "" };

export default function Admin() {
  const location = useLocation();
  const { user, setUser } = useAuth();
  const me = location.state?.user || user;

  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileView, setProfileView] = useState(false);
  const [profile, setProfile] = useState({ name: "", presentation: "", portrait: "" });
  const [cropTarget, setCropTarget] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropArea, setCropArea] = useState(null);
  const [cropping, setCropping] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/posts");
      setPosts(r.data);
    } catch {
      toast.error("Kunde inte hämta reportagen");
    }
  }, []);

  useEffect(() => {
    if (!me) return;
    load();
    api
      .get("/profile")
      .then((r) => {
        if (r.data?.name) setProfile(r.data);
      })
      .catch(() => {});
  }, [me, load]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/redaktion";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    window.history.replaceState({}, "", "/redaktion");
  };

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const startEdit = (post) => {
    setForm({
      title: post.title,
      category: post.category,
      excerpt: post.excerpt || "",
      body: post.body,
      images: post.images || [],
      published: post.published,
      featured: post.featured || false,
      caption: post.caption || "",
    });
    setEditing(post.id);
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((p) => ({ ...p, images: [...p.images, r.data.path] }));
      } catch {
        toast.error(`Kunde inte ladda upp ${f.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeImage = (path) => {
    setForm((p) => ({ ...p, images: p.images.filter((i) => i !== path) }));
  };

  const onPortraitUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, portrait: r.data.path }));
    } catch {
      toast.error("Kunde inte ladda upp bilden");
    }
    setUploading(false);
    e.target.value = "";
  };

  const saveProfile = async () => {
    if (!profile.name.trim()) {
      toast.error("Ange ditt namn");
      return;
    }
    setSaving(true);
    try {
      await api.put("/admin/profile", profile);
      toast.success("Profilen är sparad och syns nu på landningssidan");
    } catch {
      toast.error("Kunde inte spara profilen");
    } finally {
      setSaving(false);
    }
  };

  const moveImage = (index, dir) => {
    setForm((p) => {
      const imgs = [...p.images];
      const j = index + dir;
      if (j < 0 || j >= imgs.length) return p;
      [imgs[index], imgs[j]] = [imgs[j], imgs[index]];
      return { ...p, images: imgs };
    });
  };

  const openCrop = (path) => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCropArea(null);
    setCropTarget(path);
  };

  const applyCrop = async () => {
    if (!cropTarget || !cropArea) return;
    setCropping(true);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = "anonymous";
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = `${API}/files/${cropTarget}`;
      });
      const canvas = document.createElement("canvas");
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;
      canvas
        .getContext("2d")
        .drawImage(img, cropArea.x, cropArea.y, cropArea.width, cropArea.height, 0, 0, cropArea.width, cropArea.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      const fd = new FormData();
      fd.append("file", blob, "beskuren.jpg");
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, images: p.images.map((x) => (x === cropTarget ? r.data.path : x)) }));
      setCropTarget(null);
      toast.success("Bilden är beskuren");
    } catch {
      toast.error("Kunde inte beskära bilden");
    } finally {
      setCropping(false);
    }
  };

  const generateCaption = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Skriv titel och brödtext först");
      return;
    }
    setGenerating(true);
    setForm((p) => ({ ...p, caption: "" }));
    try {
      const res = await fetch(`${API}/ai/caption`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ title: form.title, category: form.category, body: form.body }),
      });
      if (!res.ok) throw new Error("AI-fel");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { text } = JSON.parse(payload);
            if (text) setForm((p) => ({ ...p, caption: p.caption + text }));
          } catch {}
        }
      }
    } catch {
      toast.error("Kunde inte skapa delningstext just nu");
    } finally {
      setGenerating(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(form.caption);
      toast.success("Delningstext kopierad");
    } catch {
      toast.error("Kunde inte kopiera");
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Titel och brödtext krävs");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        excerpt: form.excerpt,
        body: form.body,
        images: form.images,
        published: form.published,
        featured: form.featured,
        caption: form.caption,
      };
      if (editing === "new") {
        await api.post("/admin/posts", payload);
        toast.success("Reportaget är sparat");
      } else {
        await api.put(`/admin/posts/${editing}`, payload);
        toast.success("Reportaget är uppdaterat");
      }
      setEditing(null);
      await load();
    } catch {
      toast.error("Kunde inte spara reportaget");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Ta bort "${post.title}"? Det går inte att ångra.`)) return;
    try {
      await api.delete(`/admin/posts/${post.id}`);
      toast.success("Reportaget är borttaget");
      await load();
    } catch {
      toast.error("Kunde inte ta bort reportaget");
    }
  };

  if (me === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F1412]" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#C98755]" />
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F1412] px-6" data-testid="admin-login-page">
        <div className="w-full max-w-md border border-[#28332E] bg-[#161D1A] p-10 md:p-14">
          <div className="flex items-center gap-2 text-[#C98755]">
            <Waves className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-serif text-xl font-semibold">Sportfiskevatten</span>
          </div>
          <h1 className="mt-8 font-serif text-3xl font-medium tracking-tight text-[#EBE9E0]">Redaktionen</h1>
          <p className="mt-4 text-sm leading-relaxed text-[#8A998E]">
            Logga in med ditt Google-konto för att skapa reportage, ladda upp bilder och ta fram delningstexter.
            Redaktionen är låst till sidans ägare.
          </p>
          {location.state?.authError && (
            <p data-testid="auth-error-message" className="mt-6 border border-red-400/40 bg-red-400/10 px-4 py-3 text-sm leading-relaxed text-red-300">
              {location.state.authError}
            </p>
          )}
          <Button
            onClick={login}
            data-testid="google-login-button"
            className="mt-10 w-full rounded-full bg-[#C98755] py-6 text-sm font-semibold text-[#0F1412] transition-colors duration-300 hover:bg-[#EBE9E0]"
          >
            Logga in med Google
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1412] text-[#EBE9E0]" data-testid="admin-dashboard">
      <header className="border-b border-[#28332E]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-2 text-[#C98755]">
            <Waves className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-serif text-xl font-semibold">Redaktionen</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-[#8A998E] sm:inline" data-testid="admin-user-email">{me.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 text-sm text-[#8A998E] transition-colors duration-300 hover:text-[#C98755]"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} /> Logga ut
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        {profileView ? (
          <div data-testid="profile-editor">
            <button
              onClick={() => setProfileView(false)}
              data-testid="profile-back-button"
              className="flex items-center gap-2 text-sm text-[#8A998E] transition-colors duration-300 hover:text-[#C98755]"
            >
              <ArrowLeft className="h-4 w-4" /> Tillbaka till listan
            </button>
            <h1 className="mt-6 font-serif text-3xl font-medium tracking-tight">Min profil</h1>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#8A998E]">
              Namnet, texten och bilden visas i "Vem är jag?"-sektionen på landningssidan.
            </p>
            <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-3">
              <div className="space-y-8 lg:col-span-2">
                <div>
                  <label className="overline-label mb-3 block text-[#8A998E]">Ditt namn</label>
                  <Input
                    value={profile.name}
                    onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                    data-testid="profile-name-input"
                    placeholder="T.ex. Anna Lindström"
                    className="border-[#28332E] bg-[#161D1A] text-[#EBE9E0] placeholder:text-[#8A998E]/50"
                  />
                </div>
                <div>
                  <label className="overline-label mb-3 block text-[#8A998E]">Presentation</label>
                  <Textarea
                    value={profile.presentation}
                    onChange={(e) => setProfile((p) => ({ ...p, presentation: e.target.value }))}
                    data-testid="profile-presentation-textarea"
                    placeholder="Berätta kort vem du är… (tom rad ger nytt stycke)"
                    rows={10}
                    className="border-[#28332E] bg-[#161D1A] leading-relaxed text-[#EBE9E0] placeholder:text-[#8A998E]/50"
                  />
                </div>
              </div>
              <div>
                <label className="overline-label mb-3 block text-[#8A998E]">Porträttfoto</label>
                <label
                  data-testid="profile-portrait-upload"
                  className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-[#28332E] bg-[#161D1A] px-6 py-10 text-sm text-[#8A998E] transition-colors duration-300 hover:border-[#C98755] hover:text-[#C98755]"
                >
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" strokeWidth={1.5} />}
                  {uploading ? "Laddar upp…" : "Välj ett foto"}
                  <input type="file" accept="image/*" className="hidden" onChange={onPortraitUpload} data-testid="profile-portrait-input" />
                </label>
                {profile.portrait && (
                  <div className="mt-4 overflow-hidden border border-[#28332E]">
                    <img
                      src={`${API}/files/${profile.portrait}`}
                      alt="Porträtt"
                      data-testid="profile-portrait-preview"
                      className="aspect-[4/5] w-full object-cover"
                    />
                  </div>
                )}
                <Button
                  onClick={saveProfile}
                  disabled={saving}
                  data-testid="profile-save-button"
                  className="mt-8 w-full rounded-full bg-[#C98755] py-5 text-sm font-semibold text-[#0F1412] transition-colors duration-300 hover:bg-[#EBE9E0]"
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Spara profil
                </Button>
              </div>
            </div>
          </div>
        ) : editing === null ? (
          <>
            <div className="flex items-center justify-between">
              <h1 className="font-serif text-3xl font-medium tracking-tight" data-testid="admin-posts-heading">Mina reportage</h1>
              <div className="flex gap-3">
                <Button
                  onClick={() => setProfileView(true)}
                  data-testid="profile-button"
                  className="rounded-full border border-[#C98755] bg-transparent px-6 text-sm font-semibold text-[#C98755] transition-colors duration-300 hover:bg-[#C98755] hover:text-[#0F1412]"
                >
                  <User className="mr-2 h-4 w-4" /> Min profil
                </Button>
                <Button
                  onClick={startNew}
                  data-testid="new-post-button"
                  className="rounded-full bg-[#C98755] px-6 text-sm font-semibold text-[#0F1412] transition-colors duration-300 hover:bg-[#EBE9E0]"
                >
                  <Plus className="mr-2 h-4 w-4" /> Nytt reportage
                </Button>
              </div>
            </div>
            <div className="mt-10 divide-y divide-[#28332E] border border-[#28332E]">
              {posts.length === 0 && (
                <p className="p-10 font-serif text-xl italic text-[#8A998E]" data-testid="admin-empty-state">
                  Inga reportage ännu. Tänd brasan och skriv det första.
                </p>
              )}
              {posts.map((post) => (
                <div key={post.id} className="flex items-center justify-between gap-4 p-5" data-testid={`admin-post-row-${post.slug}`}>
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 font-medium text-[#EBE9E0]">
                      {post.featured && (
                        <Star className="h-3.5 w-3.5 shrink-0 fill-[#C98755] text-[#C98755]" data-testid={`featured-star-${post.slug}`} />
                      )}
                      <span className="truncate">{post.title}</span>
                    </p>
                    <p className="mt-1 text-xs tracking-widest text-[#8A998E]">
                      {CATEGORY_LABELS[post.category]} · {formatDate(post.created_at)} ·{" "}
                      <span className={post.published ? "text-[#C98755]" : "text-[#8A998E]"}>
                        {post.published ? "Publicerat" : "Utkast"}
                      </span>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      onClick={() => startEdit(post)}
                      data-testid={`edit-post-${post.slug}`}
                      className="rounded-full border border-[#28332E] p-2.5 text-[#8A998E] transition-colors duration-300 hover:border-[#C98755] hover:text-[#C98755]"
                      aria-label="Redigera"
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => remove(post)}
                      data-testid={`delete-post-${post.slug}`}
                      className="rounded-full border border-[#28332E] p-2.5 text-[#8A998E] transition-colors duration-300 hover:border-red-400 hover:text-red-400"
                      aria-label="Ta bort"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div data-testid="post-editor">
            <button
              onClick={() => setEditing(null)}
              data-testid="editor-back-button"
              className="flex items-center gap-2 text-sm text-[#8A998E] transition-colors duration-300 hover:text-[#C98755]"
            >
              <ArrowLeft className="h-4 w-4" /> Tillbaka till listan
            </button>
            <h1 className="mt-6 font-serif text-3xl font-medium tracking-tight">
              {editing === "new" ? "Nytt reportage" : "Redigera reportage"}
            </h1>

            <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-3">
              <div className="space-y-8 lg:col-span-2">
                <div>
                  <label className="overline-label mb-3 block text-[#8A998E]">Titel</label>
                  <Input
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    data-testid="editor-title-input"
                    placeholder="T.ex. Gryningsmete vid Björksjön"
                    className="border-[#28332E] bg-[#161D1A] text-[#EBE9E0] placeholder:text-[#8A998E]/50"
                  />
                </div>
                <div>
                  <label className="overline-label mb-3 block text-[#8A998E]">Ingress (valfri)</label>
                  <Input
                    value={form.excerpt}
                    onChange={(e) => setForm((p) => ({ ...p, excerpt: e.target.value }))}
                    data-testid="editor-excerpt-input"
                    placeholder="En kort inledande mening"
                    className="border-[#28332E] bg-[#161D1A] text-[#EBE9E0] placeholder:text-[#8A998E]/50"
                  />
                </div>
                <div>
                  <label className="overline-label mb-3 block text-[#8A998E]">Brödtext</label>
                  <Textarea
                    value={form.body}
                    onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                    data-testid="editor-body-textarea"
                    placeholder="Berätta historien… (tom rad ger nytt stycke)"
                    rows={14}
                    className="border-[#28332E] bg-[#161D1A] leading-relaxed text-[#EBE9E0] placeholder:text-[#8A998E]/50"
                  />
                </div>
                <div>
                  <label className="overline-label mb-3 block text-[#8A998E]">Bilder</label>
                  <label
                    data-testid="image-upload-zone"
                    className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-[#28332E] bg-[#161D1A] px-6 py-10 text-sm text-[#8A998E] transition-colors duration-300 hover:border-[#C98755] hover:text-[#C98755]"
                  >
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" strokeWidth={1.5} />}
                    {uploading ? "Laddar upp…" : "Välj bilder från datorn eller telefonen"}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="image-upload-input" />
                  </label>
                  {form.images.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="uploaded-images-grid">
                      {form.images.map((img, i) => (
                        <div key={img} className="group relative overflow-hidden border border-[#28332E]">
                          <img src={`${API}/files/${img}`} alt={`Bild ${i + 1}`} className="aspect-square w-full object-cover" />
                          {i === 0 && (
                            <span className="absolute left-2 top-2 bg-[#C98755] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#0F1412]">
                              Omslag
                            </span>
                          )}
                          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-[#0F1412]/85 py-2 opacity-100 transition-opacity duration-300 sm:opacity-0 sm:group-hover:opacity-100">
                            <button
                              onClick={() => moveImage(i, -1)}
                              disabled={i === 0}
                              data-testid={`move-image-left-${i}`}
                              className="p-1.5 text-[#EBE9E0] transition-colors duration-300 hover:text-[#C98755] disabled:opacity-30"
                              aria-label="Flytta vänster"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openCrop(img)}
                              data-testid={`crop-image-${i}`}
                              className="p-1.5 text-[#EBE9E0] transition-colors duration-300 hover:text-[#C98755]"
                              aria-label="Beskär bild"
                            >
                              <Crop className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => removeImage(img)}
                              data-testid={`remove-image-${i}`}
                              className="p-1.5 text-[#EBE9E0] transition-colors duration-300 hover:text-red-400"
                              aria-label="Ta bort bild"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => moveImage(i, 1)}
                              disabled={i === form.images.length - 1}
                              data-testid={`move-image-right-${i}`}
                              className="p-1.5 text-[#EBE9E0] transition-colors duration-300 hover:text-[#C98755] disabled:opacity-30"
                              aria-label="Flytta höger"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-8">
                <div className="border border-[#28332E] bg-[#161D1A] p-6">
                  <label className="overline-label mb-3 block text-[#8A998E]">Kategori</label>
                  <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                    <SelectTrigger data-testid="editor-category-select" className="border-[#28332E] bg-[#0F1412] text-[#EBE9E0]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-[#28332E] bg-[#161D1A] text-[#EBE9E0]">
                      <SelectItem value="fiske" data-testid="category-option-fiske">Fiske</SelectItem>
                      <SelectItem value="skapande" data-testid="category-option-skapande">Skapande</SelectItem>
                      <SelectItem value="ovrigt" data-testid="category-option-ovrigt">Övrigt</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="mt-6 flex items-center justify-between">
                    <label className="text-sm text-[#8A998E]">Publicera direkt</label>
                    <Switch
                      checked={form.published}
                      onCheckedChange={(v) => setForm((p) => ({ ...p, published: v }))}
                      data-testid="editor-publish-switch"
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <label className="text-sm text-[#8A998E]">Utvalt reportage (stor plats på förstasidan)</label>
                    <Switch
                      checked={form.featured}
                      onCheckedChange={(v) => setForm((p) => ({ ...p, featured: v }))}
                      data-testid="editor-featured-switch"
                    />
                  </div>
                  <Button
                    onClick={save}
                    disabled={saving}
                    data-testid="editor-save-button"
                    className="mt-8 w-full rounded-full bg-[#C98755] py-5 text-sm font-semibold text-[#0F1412] transition-colors duration-300 hover:bg-[#EBE9E0]"
                  >
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {editing === "new" ? "Spara reportage" : "Spara ändringar"}
                  </Button>
                </div>

                <div className="border border-[#28332E] bg-[#161D1A] p-6">
                  <div className="flex items-center justify-between">
                    <label className="overline-label text-[#8A998E]">Delningstext</label>
                    <button
                      onClick={copyCaption}
                      data-testid="copy-caption-button"
                      className="flex items-center gap-1.5 text-xs text-[#8A998E] transition-colors duration-300 hover:text-[#C98755]"
                    >
                      <Copy className="h-3.5 w-3.5" /> Kopiera
                    </button>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-[#8A998E]/70">
                    Låt AI:n föreslå texter för Facebook och Instagram utifrån reportaget.
                  </p>
                  <Button
                    onClick={generateCaption}
                    disabled={generating}
                    data-testid="ai-caption-button"
                    className="mt-5 w-full rounded-full border border-[#C98755] bg-transparent py-5 text-sm font-semibold text-[#C98755] transition-colors duration-300 hover:bg-[#C98755] hover:text-[#0F1412]"
                  >
                    {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />}
                    {generating ? "Skriver…" : "Skapa med AI"}
                  </Button>
                  <Textarea
                    value={form.caption}
                    onChange={(e) => setForm((p) => ({ ...p, caption: e.target.value }))}
                    data-testid="caption-textarea"
                    placeholder="Delningstexterna dyker upp här…"
                    rows={9}
                    className="mt-4 border-[#28332E] bg-[#0F1412] text-sm leading-relaxed text-[#EBE9E0] placeholder:text-[#8A998E]/50"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {cropTarget && (
        <div className="fixed inset-0 z-[70] flex flex-col bg-[#0F1412]/95 p-6" data-testid="crop-modal">
          <div className="mx-auto flex w-full max-w-3xl items-center justify-between py-4">
            <p className="overline-label text-[#8A998E]">Beskär bild</p>
            <button
              onClick={() => setCropTarget(null)}
              data-testid="crop-cancel-button"
              className="text-[#8A998E] transition-colors duration-300 hover:text-[#C98755]"
              aria-label="Avbryt beskärning"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="relative mx-auto w-full max-w-3xl flex-1 overflow-hidden border border-[#28332E]">
            <Cropper
              image={`${API}/files/${cropTarget}`}
              crop={crop}
              zoom={zoom}
              aspect={4 / 5}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCropArea(pixels)}
            />
          </div>
          <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-6 py-6">
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-48 accent-[#C98755]"
              data-testid="crop-zoom-slider"
              aria-label="Zooma bild"
            />
            <Button
              onClick={applyCrop}
              disabled={cropping}
              data-testid="crop-apply-button"
              className="rounded-full bg-[#C98755] px-8 text-sm font-semibold text-[#0F1412] transition-colors duration-300 hover:bg-[#EBE9E0]"
            >
              {cropping ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Använd beskärning
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
